import type { AssignedSession, Difficulty, Question, StudentIdentity } from '../types'
import { shuffle } from './random'

const difficultyOrder: Difficulty[] = ['Básica', 'Intermedia', 'Avanzada']

function difficultyPriority(preferred: StudentIdentity['support']['preferredDifficulty'] | AssignedSession['difficulty']): Difficulty[] {
  if (preferred === 'Básica') return ['Básica', 'Intermedia', 'Avanzada']
  if (preferred === 'Intermedia') return ['Intermedia', 'Básica', 'Avanzada']
  if (preferred === 'Avanzada') return ['Avanzada', 'Intermedia', 'Básica']
  return difficultyOrder
}

function balancedSelection(
  pool: Question[],
  count: number,
  priorities: Difficulty[],
  random: () => number,
): Question[] {
  const buckets = new Map<string, Question[]>()
  for (const question of pool) {
    const key = `${question.oa}::${question.topic}`
    const current = buckets.get(key) ?? []
    current.push(question)
    buckets.set(key, current)
  }

  const orderedBuckets = shuffle([...buckets.entries()], random).map(([key, questions]) => {
    const byDifficulty = priorities.flatMap((difficulty) =>
      shuffle(questions.filter((question) => question.difficulty === difficulty), random),
    )
    return { key, questions: byDifficulty }
  })

  const selected: Question[] = []
  let cursor = 0
  while (selected.length < count && orderedBuckets.some((bucket) => bucket.questions.length > 0)) {
    const bucket = orderedBuckets[cursor % orderedBuckets.length]
    const question = bucket?.questions.shift()
    if (question) selected.push(question)
    cursor += 1
  }
  return selected
}

export function selectAssignedQuestions(
  questions: Question[],
  session: AssignedSession,
  student: StudentIdentity,
  random: () => number = Math.random,
): Question[] {
  let pool = questions.filter((question) => question.unit === session.unit)
  if (session.selectedOAs.length > 0) pool = pool.filter((question) => session.selectedOAs.includes(question.oa))
  if (session.topics.length > 0) pool = pool.filter((question) => session.topics.includes(question.topic))

  const requestedCount = Math.min(student.support.questionLimit ?? session.questionCount, session.questionCount)
  const count = Math.min(requestedCount, pool.length)
  if (count <= 0) return []

  const preferred = student.support.preferredDifficulty ?? session.difficulty

  if (session.orderMode === 'progressive' || preferred === 'Progresiva') {
    const groups = difficultyOrder.map((difficulty) => shuffle(pool.filter((question) => question.difficulty === difficulty), random))
    const selected: Question[] = []
    let cursor = 0
    while (selected.length < count && groups.some((group) => group.length > 0)) {
      const group = groups[cursor % groups.length]
      const question = group?.shift()
      if (question) selected.push(question)
      cursor += 1
    }
    return session.orderMode === 'random' ? shuffle(selected, random) : selected
  }

  return shuffle(balancedSelection(pool, count, difficultyPriority(preferred), random), random)
}
